/**
 * @fileoverview Language selector component.
 * @summary Custom dropdown language selector that opens downward.
 *
 * @package KaraoNow
 * @module apps/front/src/components/LanguageSelector
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FlagIcon from './FlagIcon.js'
import  { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../utils/i18n/index.js'
import { useAppDispatch, useAppSelector } from '../hooks/redux.js'
import { updatePreferences } from '../store/slices/authSlice.js'

const languages: { code: SupportedLanguage; label: string }[] = SUPPORTED_LANGUAGES.map((code) => {
  const labels: Record<string, string> = {
    fr: 'Français',
    en: 'English',
    es: 'Español',
    it: 'Italiano',
    pt: 'Português',
    ja: '日本語',
    zh: '中文',
    ko: '한국어',
  }
  return { code, label: labels[code] ?? code }
})

/**
 * @summary Language selector component.
 * @returns {React.ReactElement} Language selector component.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function LanguageSelector() {
  const { i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currentLanguage =
    languages.find((l) => l.code === (i18n.language as SupportedLanguage)) || languages[0]

  const changeLanguage = (lang: SupportedLanguage) => {
    if (isAuthenticated) {
      dispatch(updatePreferences({ profile: { preferredLanguage: lang } }))
    }
    i18n.changeLanguage(lang)
    localStorage.setItem('i18nextLng', lang)
    setIsOpen(false)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <div className="header-lang-wrapper" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="header-lang-select-button flex items-center justify-center"
        aria-label="Select language"
        title="Select language"
      >
        <FlagIcon code={currentLanguage.code} className="w-4 h-3" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-60" onClick={() => setIsOpen(false)} />
          <div className="absolute right-1 z-70 mt-2 w-30 px-2 rounded-b-lg  bg-(--color-bg-secondary) shadow-2xl">
            <div className="py-1">
              {languages.map((language) => (
                <button
                  key={language.code}
                  type="button"
                  onClick={() => changeLanguage(language.code)}
                  className={`btn-flag ${
                    i18n.language === language.code
                      ? 'bg-red-50 text-red-700 font-semibold'
                      : 'text-(--color-text-secondary) hover:bg-(--color-bg-tertiary)'
                  }`}
                >
                  <FlagIcon code={language.code}  />
                  <span>{language.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
